/**
 * Auth Callback Page
 *
 * Handles the redirect after Auth0 authentication.
 * Shows loading state or error message as appropriate.
 */

import { useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle, ArrowLeft } from 'lucide-react';
import { consumeReturnTo } from '../../lib/return-to';

export function CallbackPage() {
  const { isAuthenticated, isLoading, error } = useAuth0();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading) {
      if (isAuthenticated) {
        // G-12: back to the deep link LoginPage stored, else the dashboard.
        navigate(consumeReturnTo() ?? '/dashboard', { replace: true });
      } else if (error) {
        console.error('Auth error:', error);
        // Keep showing error state, don't auto-redirect
      }
    }
  }, [isAuthenticated, isLoading, error, navigate]);

  if (error) {
    return (
      <div className="w-full max-w-sm">
        <div className="rounded-2xl bg-white p-8 shadow-xl border border-gray-100">
          {/* Error Icon */}
          <div className="flex justify-center mb-5">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-50">
              <AlertCircle size={28} className="text-red-500" />
            </div>
          </div>

          {/* Error Message */}
          <h2 className="text-xl font-bold text-gray-900 text-center mb-2">
            Authentication Failed
          </h2>
          <p className="text-sm text-gray-500 text-center mb-6">
            {error.message || 'An unexpected error occurred during sign in.'}
          </p>

          {/* Action */}
          <button
            onClick={() => navigate('/login', { replace: true })}
            className="
              w-full inline-flex items-center justify-center gap-2
              rounded-xl bg-guild-green px-5 py-3
              text-sm font-semibold text-white
              shadow-lg shadow-guild-green/20
              hover:bg-guild-green/95 hover:shadow-xl hover:shadow-guild-green/25
              active:scale-[0.98]
              transition-all duration-200
            "
          >
            <ArrowLeft size={18} />
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-sm">
      <div className="rounded-2xl bg-white p-8 shadow-xl border border-gray-100 text-center">
        {/* Loading Spinner */}
        <div className="flex justify-center mb-5">
          <div className="relative">
            <div className="h-14 w-14 rounded-full bg-guild-mint/30" />
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 size={28} className="text-guild-green animate-spin" />
            </div>
          </div>
        </div>

        {/* Message */}
        <h2 className="text-lg font-bold text-gray-900 mb-2">
          Completing sign in...
        </h2>
        <p className="text-sm text-gray-500">
          Please wait while we verify your credentials.
        </p>

        {/* Progress dots */}
        <div className="flex justify-center gap-1.5 mt-6">
          <div className="h-2 w-2 rounded-full bg-guild-green animate-pulse" />
          <div className="h-2 w-2 rounded-full bg-guild-green/60 animate-pulse [animation-delay:150ms]" />
          <div className="h-2 w-2 rounded-full bg-guild-green/30 animate-pulse [animation-delay:300ms]" />
        </div>
      </div>
    </div>
  );
}
